import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Search, Reply, Clock, CheckCircle2, X, Send, AlertCircle, Edit2, Trash2, MessageCircle, Plus, Users, Check, CheckCheck } from 'lucide-react';
import { useSupportTickets, SupportTicket } from '@/shared/hooks/useSupportTickets';
import { useChatRooms } from '@/shared/hooks/useChatRooms';
import { useChatMessages } from '@/shared/hooks/useChatMessages';
import { useUsers } from '@/shared/hooks/useUsers';
import { useAuth } from '@/shared/hooks/useAuth';
import { cn } from '@/shared/lib/utils';
import { Timestamp } from 'firebase/firestore';

const AdminSupportPage = () => {
    const { tickets, loading, replyToTicket, updateReply, deleteReply, deleteTicket } = useSupportTickets();
    const { chatRooms, createChatRoom, closeChatRoom, markAsRead } = useChatRooms('admin');
    const { users } = useUsers();
    const { user } = useAuth();

    // Tab state
    const [activeTab, setActiveTab] = useState<'tickets' | 'chat'>('tickets');

    // Ticket states
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
    const [replyText, setReplyText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isEditingReply, setIsEditingReply] = useState(false);

    // Chat states
    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
    const [chatMessage, setChatMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const { messages, sendMessage } = useChatMessages(selectedRoom || undefined);
    const selectedChatRoom = chatRooms.find(room => room.id === selectedRoom);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Mark as read when room is opened
    useEffect(() => {
        if (selectedRoom && selectedChatRoom && selectedChatRoom.unreadCountAdmin > 0) {
            markAsRead(selectedRoom, 'admin');
        }
    }, [selectedRoom, selectedChatRoom]);

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.content.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const pendingCount = tickets.filter(t => t.status === 'pending').length;

    const handleReply = async () => {
        if (!selectedTicket || !replyText.trim() || !user) return;

        setSubmitting(true);
        const result = isEditingReply
            ? await updateReply(selectedTicket.id, replyText, user.displayName || user.email?.split('@')[0] || 'Admin')
            : await replyToTicket(selectedTicket.id, replyText, user.displayName || user.email?.split('@')[0] || 'Admin');
        setSubmitting(false);

        if (result.success) {
            alert(isEditingReply ? '답변이 수정되었습니다.' : '답변이 성공적으로 전송되었습니다.');
            setSelectedTicket(null);
            setReplyText('');
            setIsEditingReply(false);
        } else {
            alert('답변 처리 중 오류가 발생했습니다.');
        }
    };

    const handleEditReply = (ticket: SupportTicket) => {
        setSelectedTicket(ticket);
        setReplyText(ticket.adminReply || '');
        setIsEditingReply(true);
    };

    const handleDeleteReply = async (ticketId: string) => {
        if (!confirm('답변을 삭제하시겠습니까? 문의 상태가 "대기 중"으로 변경됩니다.')) return;

        const result = await deleteReply(ticketId);
        if (result.success) {
            alert('답변이 삭제되었습니다.');
            setSelectedTicket(null);
        } else {
            alert('답변 삭제 중 오류가 발생했습니다.');
        }
    };

    const handleDeleteTicket = async (ticketId: string) => {
        if (!confirm('이 문의를 종료하시겠습니까?')) return;

        const result = await deleteTicket(ticketId);
        if (result.success) {
            alert('문의가 종료되었습니다.');
        } else {
            alert('문의 종료 중 오류가 발생했습니다.');
        }
    };

    // Chat handlers
    const handleStartChat = async (userId: string, userName: string, userEmail: string) => {
        if (!user) return;

        const result = await createChatRoom(
            userId,
            userName,
            userEmail,
            'admin',
            user.uid,
            user.displayName || user.email?.split('@')[0] || 'Admin'
        );

        if (result.success) {
            setShowUserModal(false);
            setSelectedRoom(result.roomId || null);
            setActiveTab('chat');
        } else {
            alert('채팅방 생성에 실패했습니다.');
        }
    };

    const handleSendChatMessage = async () => {
        if (!chatMessage.trim() || !selectedRoom || !user) return;

        setSendingMessage(true);
        const result = await sendMessage(
            selectedRoom,
            chatMessage,
            user.uid,
            user.displayName || user.email?.split('@')[0] || 'Admin',
            'admin'
        );

        if (result.success) {
            setChatMessage('');
        } else {
            alert('메시지 전송에 실패했습니다.');
        }
        setSendingMessage(false);
    };

    const formatTime = (timestamp?: Timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate();
        const now = new Date();
        const diff = now.getTime() - date.getTime();

        if (diff < 60000) return '방금 전';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
        if (diff < 86400000) return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
        return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
    };

    const getCategoryColor = (type: string) => {
        switch (type) {
            case 'General': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case 'Account': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
            case 'Finance': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
            case 'Technical': return 'bg-rose-500/10 text-rose-500 border-rose-500/20';
            default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return { icon: Clock, text: '대기 중', color: 'text-amber-500' };
            case 'in-progress':
                return { icon: AlertCircle, text: '처리 중', color: 'text-blue-500' };
            case 'answered':
                return { icon: CheckCircle2, text: '답변 완료', color: 'text-emerald-500' };
            case 'closed':
                return { icon: CheckCircle2, text: '종료', color: 'text-gray-500' };
            default:
                return { icon: Clock, text: '대기 중', color: 'text-amber-500' };
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-muted-foreground">로딩 중...</div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-black tracking-tight text-white uppercase italic"> 지원 센터</h2>
                    <p className="text-slate-400 text-sm font-medium mt-1 uppercase tracking-widest">사용자 문의 관리 및 고객 만족도 향상</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Tab Buttons */}
                    <button
                        onClick={() => setActiveTab('tickets')}
                        className={cn(
                            "px-6 py-3 rounded-xl font-black text-xs uppercase transition-all flex items-center gap-2",
                            activeTab === 'tickets'
                                ? "bg-emerald-600 text-white"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                        )}
                    >
                        <MessageSquare className="h-4 w-4" />
                        1:1 문의
                    </button>
                    <button
                        onClick={() => setActiveTab('chat')}
                        className={cn(
                            "px-6 py-3 rounded-xl font-black text-xs uppercase transition-all flex items-center gap-2",
                            activeTab === 'chat'
                                ? "bg-blue-600 text-white"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                        )}
                    >
                        <MessageCircle className="h-4 w-4" />
                        실시간 채팅
                        {chatRooms.filter(r => r.unreadCountAdmin > 0).length > 0 && (
                            <span className="h-5 w-5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
                                {chatRooms.filter(r => r.unreadCountAdmin > 0).length}
                            </span>
                        )}
                    </button>

                    {/* Stats */}
                    <div className="px-4 py-2 bg-slate-800/50 rounded-xl border border-slate-700 flex items-center gap-3">
                        <div className="flex flex-col text-right">
                            <span className="text-[9px] font-black text-slate-500 uppercase">전체 문의</span>
                            <span className="text-sm font-black text-white">{tickets.length}</span>
                        </div>
                        <div className="w-px h-8 bg-slate-700 mx-1"></div>
                        <div className="flex flex-col text-right">
                            <span className="text-[9px] font-black text-amber-500 uppercase">대기 중</span>
                            <span className="text-sm font-black text-white">{pendingCount}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tickets Tab Content */}
            {activeTab === 'tickets' && (
                <div className="bg-slate-950/50 border border-slate-800 rounded-[2rem] overflow-hidden">
                    <div className="p-6 border-b border-slate-800 bg-slate-900/30 flex items-center justify-between gap-4">
                        <div className="relative flex-1 max-w-md text-left">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                            <input
                                type="text"
                                placeholder="문의 내용 또는 작성자 검색..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-800 rounded-xl py-2.5 pl-11 pr-4 text-sm text-white focus:outline-none focus:border-emerald-500/50 transition-all font-bold"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-slate-900 border border-slate-800 rounded-xl py-2.5 px-4 text-xs font-black text-slate-400 uppercase tracking-widest focus:outline-none focus:border-emerald-500/50"
                            >
                                <option value="all">전체 상태</option>
                                <option value="pending">대기 중</option>
                                <option value="in-progress">처리 중</option>
                                <option value="answered">답변 완료</option>
                                <option value="closed">종료</option>
                            </select>
                        </div>
                    </div>

                    <div className="divide-y divide-slate-800/50">
                        {filteredTickets.length === 0 ? (
                            <div className="p-12 text-center">
                                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-slate-700" />
                                <p className="text-slate-500 font-bold">문의 내역이 없습니다.</p>
                            </div>
                        ) : (
                            filteredTickets.map((ticket) => {
                                const StatusIcon = getStatusBadge(ticket.status).icon;
                                return (
                                    <div key={ticket.id} className="p-8 hover:bg-slate-800/30 transition-all cursor-pointer group flex items-start gap-6">
                                        <div className={cn(
                                            "p-4 rounded-2xl shrink-0",
                                            ticket.status === 'pending' ? 'bg-amber-500/10 text-amber-500' :
                                                ticket.status === 'answered' ? 'bg-emerald-500/10 text-emerald-500' :
                                                    'bg-blue-500/10 text-blue-500'
                                        )}>
                                            <MessageSquare className="h-6 w-6" />
                                        </div>
                                        <div className="flex-1 space-y-2 text-left">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className={cn("px-3 py-1 rounded-full text-xs font-black uppercase border", getCategoryColor(ticket.type))}>
                                                        {ticket.type === 'General' ? '일반' : ticket.type === 'Account' ? '계정' : ticket.type === 'Finance' ? '입출금' : '기술'}
                                                    </span>
                                                </div>
                                                <span className="text-[10px] font-bold text-slate-500">{ticket.createdAt?.toDate().toLocaleString('ko-KR')}</span>
                                            </div>
                                            <h3 className="text-lg font-black text-white group-hover:text-emerald-400 transition-colors uppercase leading-none tracking-tight">{ticket.subject}</h3>
                                            <p className="text-sm text-slate-400 line-clamp-2">{ticket.content}</p>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs font-bold text-slate-400">{ticket.userName} ({ticket.userEmail})</span>
                                                <span className="w-1 h-1 rounded-full bg-slate-700"></span>
                                                <div className="flex items-center gap-1.5">
                                                    <StatusIcon className={cn("h-3 w-3", getStatusBadge(ticket.status).color)} />
                                                    <span className={cn("text-[10px] font-black uppercase tracking-widest", getStatusBadge(ticket.status).color)}>
                                                        {getStatusBadge(ticket.status).text}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setSelectedTicket(ticket)}
                                            className="px-6 py-3 bg-slate-800 hover:bg-emerald-600 text-white rounded-xl text-xs font-black transition-all flex items-center gap-2 group-hover:shadow-lg group-hover:shadow-emerald-600/20 active:scale-95"
                                        >
                                            <Reply className="h-4 w-4" />
                                            {ticket.status === 'answered' ? '답변 보기' : '답변하기'}
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* Chat Tab Content */}
            {activeTab === 'chat' && (
                <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-250px)]">
                    {/* Chat Rooms List */}
                    <div className="lg:col-span-1 bg-slate-950/50 border border-slate-800 rounded-3xl overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-800 bg-slate-900/30 flex items-center justify-between">
                            <h2 className="text-lg font-black text-white uppercase">대화 목록</h2>
                            <button
                                onClick={() => setShowUserModal(true)}
                                className="h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center gap-2 transition-all"
                            >
                                <Plus className="h-4 w-4" />
                                새 채팅
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {chatRooms.length === 0 ? (
                                <div className="p-12 text-center">
                                    <MessageCircle className="h-12 w-12 mx-auto mb-4 text-slate-700" />
                                    <p className="text-slate-500 font-bold text-sm">활성 채팅이 없습니다</p>
                                </div>
                            ) : (
                                chatRooms.map((room) => (
                                    <div
                                        key={room.id}
                                        onClick={() => setSelectedRoom(room.id)}
                                        className={cn(
                                            "p-6 border-b border-slate-800/50 cursor-pointer transition-all hover:bg-slate-800/30",
                                            selectedRoom === room.id && "bg-blue-500/10 border-l-4 border-l-blue-500"
                                        )}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-sm font-black text-white truncate">
                                                        {room.userName}
                                                    </h3>
                                                    {room.unreadCountAdmin > 0 && (
                                                        <span className="h-5 w-5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
                                                            {room.unreadCountAdmin}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-400 truncate">{room.lastMessage || '메시지 없음'}</p>
                                            </div>
                                            <span className="text-[10px] text-slate-500 font-bold whitespace-nowrap">
                                                {formatTime(room.lastMessageTime)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="lg:col-span-2 bg-slate-950/50 border border-slate-800 rounded-3xl overflow-hidden flex flex-col">
                        {selectedRoom ? (
                            <>
                                <div className="p-6 border-b border-slate-800 bg-slate-900/30 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                                            <Users className="h-5 w-5 text-blue-500" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-black text-white">{selectedChatRoom?.userName}</h2>
                                            <p className="text-xs text-slate-500 font-bold">{selectedChatRoom?.userEmail}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => selectedRoom && closeChatRoom(selectedRoom)}
                                        className="px-4 py-2 bg-slate-800 hover:bg-red-600 rounded-xl text-xs font-black text-white transition-all"
                                    >
                                        채팅 종료
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                    {messages.length === 0 ? (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="text-center">
                                                <MessageCircle className="h-12 w-12 mx-auto mb-4 text-slate-700" />
                                                <p className="text-slate-500 font-bold text-sm">메시지를 시작하세요</p>
                                            </div>
                                        </div>
                                    ) : (
                                        messages.map((msg) => {
                                            const isMyMessage = msg.senderRole === 'admin';
                                            const isRead = msg.readBy.length > 1;

                                            return (
                                                <div
                                                    key={msg.id}
                                                    className={cn(
                                                        "flex gap-3",
                                                        isMyMessage ? "justify-end" : "justify-start"
                                                    )}
                                                >
                                                    <div className={cn(
                                                        "max-w-[70%] space-y-1",
                                                        isMyMessage && "items-end"
                                                    )}>
                                                        <div className={cn(
                                                            "px-4 py-3 rounded-2xl",
                                                            isMyMessage
                                                                ? "bg-blue-600 text-white"
                                                                : "bg-slate-800 text-white"
                                                        )}>
                                                            <p className="text-sm font-medium whitespace-pre-wrap break-words">
                                                                {msg.message}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1 px-2">
                                                            <span className="text-[10px] text-slate-500 font-bold">
                                                                {formatTime(msg.createdAt)}
                                                            </span>
                                                            {isMyMessage && (
                                                                isRead ?
                                                                    <CheckCheck className="h-3 w-3 text-blue-500" /> :
                                                                    <Check className="h-3 w-3 text-slate-500" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                <div className="p-6 border-t border-slate-800 bg-slate-900/30">
                                    <div className="flex gap-3">
                                        <input
                                            type="text"
                                            value={chatMessage}
                                            onChange={(e) => setChatMessage(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && !sendingMessage && handleSendChatMessage()}
                                            placeholder="메시지를 입력하세요..."
                                            className="flex-1 h-12 bg-slate-950 border border-slate-800 rounded-xl px-4 text-sm text-white outline-none focus:border-blue-500/50 font-medium"
                                        />
                                        <button
                                            onClick={handleSendChatMessage}
                                            disabled={sendingMessage || !chatMessage.trim()}
                                            className="h-12 w-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Send className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <MessageCircle className="h-16 w-16 mx-auto mb-4 text-slate-700" />
                                    <p className="text-slate-400 font-bold">대화를 선택하거나 새 채팅을 시작하세요</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* User Selection Modal */}
            {showUserModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl p-8 space-y-6 shadow-2xl max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-black uppercase text-white">사용자 선택</h2>
                            <button onClick={() => setShowUserModal(false)} className="h-10 w-10 rounded-xl hover:bg-slate-800 flex items-center justify-center text-slate-400">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-2">
                            {users.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Users className="h-12 w-12 mx-auto mb-4 text-slate-700" />
                                    <p className="text-slate-500 font-bold">승인된 사용자가 없습니다</p>
                                </div>
                            ) : (
                                users.map((targetUser) => (
                                    <div
                                        key={targetUser.uid}
                                        onClick={() => handleStartChat(targetUser.uid, targetUser.name || targetUser.email || '사용자', targetUser.email || '')}
                                        className="p-4 bg-slate-800/50 hover:bg-slate-800 rounded-xl cursor-pointer transition-all flex items-center justify-between"
                                    >
                                        <div>
                                            <h3 className="text-sm font-black text-white">{targetUser.name || '이름 없음'}</h3>
                                            <p className="text-xs text-slate-400">{targetUser.email}</p>
                                        </div>
                                        <button className="h-10 px-4 bg-blue-600 hover:bg-blue-500 rounded-xl text-xs font-black text-white transition-all">
                                            채팅 시작
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Reply Modal */}
            {selectedTicket && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-black uppercase text-white">문의 상세 및 답변</h2>
                            <button onClick={() => setSelectedTicket(null)} className="h-10 w-10 rounded-xl hover:bg-slate-800 flex items-center justify-center text-slate-400">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-4 p-6 bg-slate-950/50 rounded-2xl border border-slate-800">
                            <div className="flex items-center gap-3">
                                <span className={cn("px-3 py-1 rounded-full text-xs font-black uppercase border", getCategoryColor(selectedTicket.type))}>
                                    {selectedTicket.type === 'General' ? '일반' : selectedTicket.type === 'Account' ? '계정' : selectedTicket.type === 'Finance' ? '입출금' : '기술'}
                                </span>
                                <span className="text-xs text-slate-500 font-bold">{selectedTicket.createdAt?.toDate().toLocaleString('ko-KR')}</span>
                            </div>
                            <h3 className="text-xl font-black text-white">{selectedTicket.subject}</h3>
                            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{selectedTicket.content}</p>
                            <div className="pt-4 border-t border-slate-800 flex items-center gap-2 text-xs text-slate-400">
                                <span className="font-bold">작성자:</span>
                                <span>{selectedTicket.userName} ({selectedTicket.userEmail})</span>
                            </div>
                        </div>

                        {selectedTicket.adminReply && !isEditingReply && (
                            <div className="space-y-3 p-6 bg-emerald-950/30 rounded-2xl border border-emerald-500/20">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                    <span className="text-xs font-black text-emerald-500 uppercase">답변 완료</span>
                                    <span className="text-xs text-slate-500 ml-auto">{selectedTicket.adminRepliedAt?.toDate().toLocaleString('ko-KR')}</span>
                                </div>
                                <p className="text-sm text-white leading-relaxed whitespace-pre-wrap">{selectedTicket.adminReply}</p>
                                <p className="text-xs text-slate-400">답변자: {selectedTicket.adminName}</p>
                                <div className="flex gap-2 pt-2">
                                    <button
                                        onClick={() => handleEditReply(selectedTicket)}
                                        className="flex-1 h-10 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase flex items-center justify-center gap-2 transition-all"
                                    >
                                        <Edit2 className="h-3.5 w-3.5" />
                                        답변 수정
                                    </button>
                                    <button
                                        onClick={() => handleDeleteReply(selectedTicket.id)}
                                        className="flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-black text-xs uppercase flex items-center justify-center gap-2 transition-all"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        답변 삭제
                                    </button>
                                </div>
                            </div>
                        )}

                        {(selectedTicket.status !== 'answered' || isEditingReply) && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="block text-sm font-black uppercase text-slate-400">
                                        {isEditingReply ? '답변 수정' : '답변 작성'}
                                    </label>
                                    {isEditingReply && (
                                        <button
                                            onClick={() => {
                                                setIsEditingReply(false);
                                                setReplyText('');
                                                setSelectedTicket(null);
                                            }}
                                            className="text-xs font-bold text-slate-400 hover:text-white transition-colors"
                                        >
                                            취소
                                        </button>
                                    )}
                                </div>
                                <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    rows={6}
                                    placeholder="고객에게 전달할 답변을 작성해 주세요..."
                                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white outline-none focus:border-emerald-500 resize-none font-bold"
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleReply}
                                        disabled={submitting || !replyText.trim()}
                                        className="flex-1 h-14 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-sm uppercase flex items-center justify-center gap-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Send className="h-4 w-4" />
                                        {submitting ? '처리 중...' : isEditingReply ? '수정 완료' : '답변 전송'}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteTicket(selectedTicket.id)}
                                        className="h-14 px-4 rounded-xl bg-slate-800 hover:bg-rose-600 text-white font-black text-xs uppercase flex items-center justify-center gap-2 transition-all"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        문의 종료
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSupportPage;
